import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { GlobalHttpService } from '../services/global-http.service';
import * as GlobalActions from './global.actions';

@Injectable()
export class GlobalEffects {
  constructor(
    private http: GlobalHttpService,
    private actions$: Actions,
    private _snackBar: MatSnackBar
  ) { }

  fetchAboutStory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchStory),
      switchMap((_) =>
        this.http.fetchStory().pipe(
          map((response) => GlobalActions.fetchStorySuccess({ response })),
          catchError((err) => of(GlobalActions.fetchStoryError(err)))
        )
      )
    )
  );

  fetchAboutLeadership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchLeadership),
      switchMap((_) =>
        this.http.fetchLeadership().pipe(
          map((response) => GlobalActions.fetchLeadershipSuccess({ response })),
          catchError((err) => of(GlobalActions.fetchLeadershipError(err)))
        )
      )
    )
  );

  fetchAboutPolicy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchPolicy),
      switchMap((_) =>
        this.http.fetchPolicy().pipe(
          map((response) => GlobalActions.fetchPolicySuccess({ response })),
          catchError((err) => of(GlobalActions.fetchPolicyError(err)))
        )
      )
    )
  );

  fetchAboutAccreditation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchAccreditation),
      switchMap((_) =>
        this.http.fetchAccreditation().pipe(
          map((response) => GlobalActions.fetchAccreditationSuccess({ response })),
          catchError((err) => of(GlobalActions.fetchAccreditationError(err)))
        )
      )
    )
  );

  loadTranslation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.loadTranslation),
      switchMap((action) =>
        this.http.getTranslation(action.language).pipe(
          map((data) => GlobalActions.loadTranslationSuccess({ data })),
          catchError((err) => of(GlobalActions.loadTranslationError()))
        )
      )
    )
  );

  sendEmail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.sendEmail),
      switchMap((action) =>
        this.http.sendEmail(action.emailData).pipe(
          map(() => GlobalActions.sendEmailSuccess()),
          catchError((error) => of(GlobalActions.sendEmailError(error)))
        )
      ),
      tap((action) => this.openEmailSnackBar(action))
    )
  );

  sendResume$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.sendResume),
      switchMap((action) =>
        this.http.sendResume(action.formData).pipe(
          map(() => GlobalActions.sendResumeSuccess()),
          catchError((error) => of(GlobalActions.sendResumeError(error)))
        )
      ),
      tap((action) => this.openEmailSnackBar(action))
    )
  );

  fetchUserProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchProfile),
      switchMap((_) =>
        this.http.profile().pipe(
          map((payload) => {
            return GlobalActions.fetchProfileSuccess({ payload });
          }),
          catchError((error) => {
            return of(GlobalActions.fetchProfileError({ error }));
          })
        )
      )
    )
  );

  fetchAllProjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchAllProjects),
      switchMap((_) =>
        this.http.fetchAllProjects().pipe(
          map((response) =>
            GlobalActions.fetchAllProjectsSuccess({ response })
          ),
          catchError((err) => of(GlobalActions.fetchAllProjectsError(err)))
        )
      )
    )
  );

  fetchAllExpertises$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchAllExpertises),
      switchMap((_) =>
        this.http.fetchAllExpertises().pipe(
          map((response) =>
            GlobalActions.fetchAllExpertisesSuccess({ response })
          ),
          catchError((err) => of(GlobalActions.fetchAllExpertisesError(err)))
        )
      )
    )
  );

  fetchExpertiseHero$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchExpertiseHero),
      switchMap((_) =>
        this.http.fetchExpertiseHero().pipe(
          map((response) => GlobalActions.fetchExpertiseHeroSuccess({ response })),
          catchError((err) => of(GlobalActions.fetchExpertiseHeroError(err)))
        )
      )
    )
  );

  fetchActiveDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchActiveDetails),
      switchMap((_) =>
        this.http.fetchActiveDetails().pipe(
          map((response) =>
            GlobalActions.fetchActiveDetailsSuccess({ response })
          ),
          catchError((err) => of(GlobalActions.fetchActiveDetailsError(err)))
        )
      )
    )
  );

  fetchHomeHero$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchHomeHero),
      switchMap((_) =>
        this.http.fetchHomeHero().pipe(
          map((response) =>
            GlobalActions.fetchHomeHeroSuccess({ response })
          ),
          catchError((err) => of(GlobalActions.fetchHomeHeroError(err)))
        )
      )
    )
  );

  deleteMedia$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.deleteMedia),
      switchMap((action) =>
        this.http.deleteMedia(action.existMedia).pipe(
          map((response) => {
            this.openSnackBar(response.message, 'Dismiss');
            return GlobalActions.deleteMediaSuccess({ response });
          }),
          catchError((error) => of(GlobalActions.deleteMediaError({ error })))
        )
      )
    )
  );

  fetchAllNews$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchAllNews),
      switchMap((_) =>
        this.http.fetchAllNews().pipe(
          map((response) => GlobalActions.fetchAllNewsSuccess({ response })),
          catchError((err) => of(GlobalActions.fetchAllNewsError(err)))
        )
      )
    )
  );

  fetchAllCareers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchAllCareers),
      switchMap((_) =>
        this.http.fetchAllCareers().pipe(
          map((response) => GlobalActions.fetchAllCareersSuccess({ response })),
          catchError((err) => of(GlobalActions.fetchAllCareersError(err)))
        )
      )
    )
  );

  deleteDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.deleteDetails),
      switchMap((action) =>
        this.http.deleteDetails(action.nameList, action.detailsId).pipe(
          map((response) => {
            this.openSnackBar(response.message, 'Dismiss');
            return GlobalActions.deleteDetailsSuccess({ response });
          }),
          catchError((error) => {
            this.openSnackBar(error.error.message, 'Dismiss');
            return of(GlobalActions.deleteDetailsError({ error }));
          })
        )
      )
    )
  );

  createDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.createDetails),
      switchMap((action) =>
        this.http.createDetails(action.details).pipe(
          map((response) => {
            this.openSnackBar(response.message, 'Dismiss');
            return GlobalActions.createDetailsSuccess({ response });
          }),
          catchError((error) => of(GlobalActions.createDetailsError({ error })))
        )
      )
    )
  );

  private openSnackBar(message: string, action: string): void {
    this._snackBar.open(message, action, {
      horizontalPosition: 'end',
      verticalPosition: 'bottom',
      duration: 5000,
    });
  }

  private openEmailSnackBar(action: any): void {
    if (action.error) {
      this._snackBar.open('Email was not sent, please try again!', 'Dismiss', {
        duration: 10000,
      });
      return;
    }

    this._snackBar.open('Email was sent successfully!', 'Dismiss', {
      duration: 10000,
    });
  }

  loadVideo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.loadVideo),
      switchMap((action) =>
        this.http.getVideo(action.fileName).pipe(
          map((video) => GlobalActions.loadVideoSuccess({ video })),
          catchError((error) => of(GlobalActions.loadVideoFailure({ error })))
        )
      )
    )
  );

  fetchContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GlobalActions.fetchContacts),
      switchMap((_) =>
        this.http.fetchContacts().pipe(
          map((response: any) =>
            GlobalActions.fetchContactsSuccess({
              response: Array.isArray(response) ? response : response?.data || [],
            })
          ),
          catchError((err) => of(GlobalActions.fetchContactsError({ error: err })))
        )
      )
    )
  );
}
